/**
 * Hook per gestire WebSocket per foto remote con fallback polling
 */

import { useState, useEffect, useRef, useCallback } from 'react';

interface Photo {
  id: string;
  url: string;
  order: number;
  comment?: string;
  uploadedAt: string;
}

interface PhotoUploadedEvent {
  photoId: string;
  photoUrl: string;
  componentId: number;
  turbineId: string;
  timestamp: string;
}

interface UsePhotoWebSocketOptions {
  turbineId: string;
  damageTypeId: number;
  sessionToken: string;
  enabled: boolean;
  onPhotoUploaded?: (photo: PhotoUploadedEvent) => void;
}

export const usePhotoWebSocket = ({
  turbineId,
  damageTypeId,
  sessionToken,
  enabled,
  onPhotoUploaded
}: UsePhotoWebSocketOptions) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [usePolling, setUsePolling] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch iniziale foto esistenti
  const fetchPhotos = useCallback(async () => {
    try {
      const response = await fetch(
        `${window.location.origin}/api/persistent-sessions/component-photos?turbineId=${turbineId}&damageTypeId=${damageTypeId}`,
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.photos) {
          console.log('[usePhotoWebSocket] 📥 Foto iniziali caricate:', data.photos.length);
          setPhotos(data.photos);
        }
      }
    } catch (err) {
      console.error('[usePhotoWebSocket] Errore fetch foto:', err);
    }
  }, [turbineId, damageTypeId, sessionToken]);

  // Polling fallback
  const startPollingFallback = useCallback(() => {
    console.log('[usePhotoWebSocket] ⚠️ WebSocket fallito, uso polling fallback');
    setUsePolling(true);

    const pollPhotos = async () => {
      try {
        const response = await fetch(
          `${window.location.origin}/api/persistent-sessions/component-photos?turbineId=${turbineId}&damageTypeId=${damageTypeId}`,
          {
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.photos) {
            setPhotos(data.photos);
          }
        }
      } catch (err) {
        console.error('[usePhotoWebSocket] Errore polling foto:', err);
      }
    };

    // Polling ogni 3 secondi
    pollingIntervalRef.current = setInterval(pollPhotos, 3000);
  }, [turbineId, damageTypeId, sessionToken]);

  // Setup WebSocket
  useEffect(() => {
    if (!enabled || !sessionToken) return;

    console.log('[usePhotoWebSocket] 🔌 Connessione WebSocket...');

    // Fetch foto iniziali
    fetchPhotos();

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);
    wsRef.current = ws;

    let authTimeout: NodeJS.Timeout;

    ws.onopen = () => {
      console.log('[usePhotoWebSocket] ✅ WebSocket connesso');
      setWsConnected(true);

      // Join room turbine-specific
      ws.send(JSON.stringify({
        type: 'join',
        room: `turbine-${turbineId}`
      }));

      // Timeout autenticazione
      authTimeout = setTimeout(() => {
        if (!wsConnected) {
          console.warn('[usePhotoWebSocket] ⚠️ Timeout connessione, uso polling');
          ws.close();
          startPollingFallback();
        }
      }, 5000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'joined':
            clearTimeout(authTimeout);
            console.log('[usePhotoWebSocket] ✅ Join room riuscito:', data.room);
            break;

          case 'photo-uploaded':
            console.log('[usePhotoWebSocket] 📸 Foto caricata via WebSocket:', data);

            // Verifica se la foto appartiene a questo componente
            if (data.componentId === damageTypeId && data.turbineId === turbineId) {
              // Ricarica tutte le foto per avere ordine e dati completi
              fetchPhotos();

              // Callback opzionale
              if (onPhotoUploaded) {
                onPhotoUploaded(data);
              }
            }
            break;

          case 'pong':
            // Keepalive
            break;

          default:
            console.log('[usePhotoWebSocket] Messaggio:', data.type);
        }
      } catch (error) {
        console.error('[usePhotoWebSocket] Errore parsing:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('[usePhotoWebSocket] ❌ Errore WebSocket:', error);
    };

    ws.onclose = () => {
      console.log('[usePhotoWebSocket] 🔌 WebSocket disconnesso');
      setWsConnected(false);

      if (enabled && !usePolling) {
        startPollingFallback();
      }
    };

    // Ping ogni 30s per keepalive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      console.log('[usePhotoWebSocket] Cleanup');
      clearTimeout(authTimeout);
      clearInterval(pingInterval);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      ws.close();
    };
  }, [enabled, turbineId, damageTypeId, sessionToken, startPollingFallback, fetchPhotos, onPhotoUploaded, wsConnected]);

  // Funzione per refresh manuale
  const refreshPhotos = useCallback(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  return {
    photos,
    wsConnected,
    usePolling,
    refreshPhotos
  };
};
